import Stripe from 'stripe'
import type {
  BillingMetrics,
  BillingInsightSnapshot,
  BillingPortalPayload,
  CheckoutSessionPayload,
  InvoiceSummary,
  ReferralCode,
  ReferralLeaderboardEntry,
  SubscriptionActivityEntry,
  SubscriptionHealth,
  SubscriptionEngagement,
  SubscriptionInsight,
  SubscriptionPlan,
  SubscriptionPaymentMethod,
  SubscriptionRecommendation,
  SubscriptionSummary,
  SubscriptionUsageBreakdown,
  UserQuota,
  UserQuotaUsage,
  UserSubscription,
  UserStatsAggregated
} from '@my-roadtrip/types'
import { isString, isNonEmptyString } from '../../common/utils/type-guards.js'
import { authService } from '../auth/auth.service.js'
import { buildDefaultPlans, findDefaultPlan, findDefaultPlanByPriceId } from './billing.constants.js'
import { BillingRepository, type UserBillingProfile } from './billing.repository.js'
import { UsersRepository } from '../users/users.repository.js'
import { createLogger } from '../../common/utils/logger.js'

const logger = createLogger('BillingService')

export type QuotaAction = 'create_plan' | 'generate_plan' | 'ai_call' | 'collaborator' | 'storage'

interface CheckoutOptions {
  successUrl?: string
  cancelUrl?: string
}

interface QuotaCheckResult {
  allowed: boolean
  quota: UserQuota
  limits: UserQuota['limits']
  usage: UserQuotaUsage
  remaining: number | null
  reason?: string
}

interface PlansCacheEntry {
  plans: SubscriptionPlan[]
  byId: Map<string, SubscriptionPlan>
  byPriceId: Map<string, SubscriptionPlan>
  fetchedAt: number
}

const STRIPE_API_VERSION = '2024-06-20' as const

function nowISO(): string {
  return new Date().toISOString()
}

function startOfCurrentMonth(): Date {
  const now = new Date()
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0, 0))
}

function endOfCurrentMonth(): Date {
  const start = startOfCurrentMonth()
  return new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1, 0, 0, 0, 0))
}

function createQuotaForPlan(plan: SubscriptionPlan): UserQuota {
  const start = startOfCurrentMonth().toISOString()
  const end = endOfCurrentMonth().toISOString()
  return {
    limits: plan.limits,
    usage: {
      plansGenerated: 0,
      aiCalls: 0,
      collaborators: 0,
      storageUsedMB: 0
    },
    window: {
      period: 'month',
      periodStart: start,
      periodEnd: end
    },
    updatedAt: nowISO()
  }
}

function getFrontendBaseUrl(): string {
  return (
    process.env.FRONTEND_URL ||
    process.env.APP_URL ||
    process.env.VITE_APP_URL ||
    'http://localhost:5173'
  )
}

export class BillingService {
  private readonly stripe: Stripe | null
  private readonly plansCacheTtlMs = 60_000
  private plansCache: PlansCacheEntry | null = null
  private plansCachePromise: Promise<PlansCacheEntry> | null = null

  constructor(
    private readonly repository: BillingRepository,
    private readonly usersRepository: UsersRepository
  ) {
    const secret = process.env.STRIPE_SECRET_KEY
    if (secret) {
      this.stripe = new Stripe(secret, { apiVersion: STRIPE_API_VERSION })
    } else {
      this.stripe = null
      logger.warn('STRIPE_SECRET_KEY missing, payments disabled')
    }
  }

  private isStripeConfigured(): boolean {
    return Boolean(this.stripe)
  }

  private normalizeCurrency(currency: string): string {
    return currency ? currency.toLowerCase() : 'eur'
  }

  private mapInterval(interval: SubscriptionPlan['billingInterval']): Stripe.PriceCreateParams.Recurring.Interval {
    return interval === 'annual' ? 'year' : 'month'
  }

  private isResourceMissing(error: unknown): boolean {
    return Boolean(error) && typeof error === 'object' && 'code' in (error as Record<string, unknown>) && (error as { code?: string }).code === 'resource_missing'
  }

  private isStripeError(error: unknown, code: string): boolean {
    return Boolean(error) && typeof error === 'object' && 'code' in (error as Record<string, unknown>) && (error as { code?: string }).code === code
  }

  private async ensureStripeProduct(stripe: Stripe, plan: SubscriptionPlan): Promise<string> {
    const metadata = {
      planId: plan.id,
      tier: plan.tier,
      interval: plan.billingInterval
    }

    if (plan.stripeProductId) {
      try {
        const product = await stripe.products.retrieve(plan.stripeProductId)
        if (product.deleted) {
          return await this.createStripeProduct(stripe, plan, metadata)
        }

        await stripe.products.update(plan.stripeProductId, {
          name: plan.name,
          description: plan.description || undefined,
          active: plan.active,
          metadata
        })

        return plan.stripeProductId
      } catch (error) {
        if (!this.isResourceMissing(error)) {
          throw error
        }
      }
    }

    return await this.createStripeProduct(stripe, plan, metadata)
  }

  private async createStripeProduct(
    stripe: Stripe,
    plan: SubscriptionPlan,
    metadata: Record<string, string>
  ): Promise<string> {
    const product = await stripe.products.create({
      name: plan.name,
      description: plan.description || undefined,
      active: plan.active,
      metadata
    })

    return product.id
  }

  private async ensureStripePrice(
    stripe: Stripe,
    plan: SubscriptionPlan,
    productId: string
  ): Promise<string | null> {
    if (plan.monthlyPrice <= 0) {
      return null
    }

    const targetAmount = Math.round(plan.monthlyPrice * 100)
    const targetCurrency = this.normalizeCurrency(plan.currency)
    const interval = this.mapInterval(plan.billingInterval)

    if (plan.stripePriceId) {
      try {
        const price = await stripe.prices.retrieve(plan.stripePriceId)
        if (
          !price.deleted &&
          price.unit_amount === targetAmount &&
          price.currency === targetCurrency &&
          price.recurring?.interval === interval &&
          price.product === productId
        ) {
          if (price.active !== plan.active) {
            await stripe.prices.update(plan.stripePriceId, { active: plan.active })
          }
          return plan.stripePriceId
        }
      } catch (error) {
        if (!this.isResourceMissing(error)) {
          throw error
        }
      }
    }

    const nickname = `${plan.name} ${interval === 'year' ? 'Annuel' : 'Mensuel'}`

    const price = await stripe.prices.create({
      nickname,
      currency: targetCurrency,
      unit_amount: targetAmount,
      recurring: {
        interval
      },
      active: plan.active,
      metadata: {
        planId: plan.id,
        tier: plan.tier,
        interval: plan.billingInterval
      },
      product: productId
    })

    return price.id
  }

  private async syncPlanWithStripe(plan: SubscriptionPlan): Promise<SubscriptionPlan> {
    if (!this.stripe) {
      throw new Error('Stripe is not configured. Configure STRIPE_SECRET_KEY to manage paid plans.')
    }

    const stripe = this.stripe
    const productId = await this.ensureStripeProduct(stripe, plan)
    const priceId = await this.ensureStripePrice(stripe, plan, productId)

    return {
      ...plan,
      stripeProductId: productId,
      stripePriceId: priceId
    }
  }

  private requireStripe(): Stripe {
    if (!this.stripe) {
      throw new Error('Stripe is not configured. Set STRIPE_SECRET_KEY to enable payments.')
    }
    return this.stripe
  }

  private async loadPlansCache(force = false): Promise<PlansCacheEntry> {
    const now = Date.now()

    if (!force && this.plansCache && now - this.plansCache.fetchedAt < this.plansCacheTtlMs) {
      return this.plansCache
    }

    if (!force && this.plansCachePromise) {
      return this.plansCachePromise
    }

    const promise = (async () => {
      try {
        const entry = await this.fetchPlansFromSource()
        this.plansCache = entry
        this.plansCachePromise = null
        return entry
      } catch (error) {
        this.plansCachePromise = null
        throw error
      }
    })()

    this.plansCachePromise = promise
    return promise
  }

  private async fetchPlansFromSource(): Promise<PlansCacheEntry> {
    const [stored, defaults] = await Promise.all([
      this.repository.listPlans(true),
      Promise.resolve(buildDefaultPlans())
    ])

    const map = new Map<string, SubscriptionPlan>()
    for (const plan of defaults) {
      map.set(plan.id, plan)
    }

    for (const plan of stored) {
      const base = map.get(plan.id)
      if (base) {
        map.set(plan.id, {
          ...base,
          ...plan,
          limits: plan.limits ?? base.limits,
          features: plan.features ?? base.features,
          stripePriceId: plan.stripePriceId ?? base.stripePriceId
        })
      } else {
        map.set(plan.id, plan)
      }
    }

    const plans = Array.from(map.values()).sort((a, b) => a.displayOrder - b.displayOrder)
    const byId = new Map<string, SubscriptionPlan>()
    const byPriceId = new Map<string, SubscriptionPlan>()

    for (const plan of plans) {
      byId.set(plan.id, plan)
      if (plan.stripePriceId) {
        byPriceId.set(plan.stripePriceId, plan)
      }
    }

    return {
      plans,
      byId,
      byPriceId,
      fetchedAt: Date.now()
    }
  }


  private async findPlanByTier(tier: SubscriptionPlan['tier']): Promise<SubscriptionPlan | null> {
    const plans = await this.listPlans()
    const matching = plans.filter(plan => plan.tier === tier)
    if (matching.length === 0) {
      return null
    }
    const monthly = matching.find(plan => plan.billingInterval === 'monthly')
    return monthly ?? matching[0]
  }

  private async resolvePlanForQuota(
    userId: string,
    options?: { planId?: string; tier?: SubscriptionPlan['tier'] }
  ): Promise<SubscriptionPlan> {
    const tier = options?.tier
    // If a tier is provided, it is the source of truth (planId may be stale)
    if (tier) {
      const byTier = await this.findPlanByTier(tier)
      if (byTier) {
        return byTier
      }
    }

    const planId = options?.planId?.trim()
    if (planId) {
      try {
        return await this.getPlan(planId)
      } catch (error) {
        logger.warn('Plan lookup failed while resetting quota', { userId, planId, error })
      }
    }

    return this.getPlan('free')
  }

  private invalidatePlansCache(): void {
    this.plansCache = null
    this.plansCachePromise = null
  }

  async listPlans(includeInactive = false): Promise<SubscriptionPlan[]> {
    const cache = await this.loadPlansCache()
    return includeInactive ? cache.plans : cache.plans.filter(plan => plan.active)
  }

  async getPlan(planId: string): Promise<SubscriptionPlan> {
    const cache = await this.loadPlansCache()
    const plan = cache.byId.get(planId)
    if (plan) {
      return plan
    }
    const fallback = findDefaultPlan(planId)
    if (fallback) {
      return fallback
    }

    throw new Error('Plan not found')
  }

  async resetQuotaForPlan(
    userId: string,
    options?: { planId?: string; tier?: SubscriptionPlan['tier'] }
  ): Promise<UserQuota> {
    const plan = await this.resolvePlanForQuota(userId, options)
    const quota = createQuotaForPlan(plan)
    await this.repository.resetQuota(userId, quota)
    return quota
  }

  async getPlanByPriceId(priceId: string): Promise<SubscriptionPlan | null> {
    if (!priceId) return null
    const cache = await this.loadPlansCache()
    const fromCache = cache.byPriceId.get(priceId)
    if (fromCache) {
      return fromCache
    }
    return findDefaultPlanByPriceId(priceId) ?? null
  }

  async upsertPlan(plan: SubscriptionPlan, options?: { syncStripe?: boolean }): Promise<SubscriptionPlan> {
    let nextPlan = { ...plan, currency: this.normalizeCurrency(plan.currency) }

    if (options?.syncStripe) {
      if (plan.monthlyPrice > 0) {
        nextPlan = await this.syncPlanWithStripe(nextPlan)
      } else {
        nextPlan = { ...nextPlan, stripePriceId: null }
      }
    }

    await this.repository.upsertPlan(nextPlan)
    this.invalidatePlansCache()
    void this.loadPlansCache(true).catch(() => undefined)
    return nextPlan
  }

  async deletePlan(planId: string): Promise<void> {
    await this.repository.deletePlan(planId)
    this.invalidatePlansCache()
    void this.loadPlansCache(true).catch(() => undefined)
  }

  async getUserPlan(userId: string): Promise<SubscriptionPlan> {
    const profile = await this.repository.getUserBillingProfile(userId)
    const planId = profile?.planId ?? 'free'
    return this.getPlan(planId)
  }

  private resolveSuccessUrl(options?: CheckoutOptions): string {
    return (
      options?.successUrl ||
      process.env.CHECKOUT_SUCCESS_URL ||
      `${getFrontendBaseUrl()}/account?success=true`
    )
  }

  private resolveCancelUrl(options?: CheckoutOptions): string {
    return (
      options?.cancelUrl ||
      process.env.CHECKOUT_CANCEL_URL ||
      `${getFrontendBaseUrl()}/subscriptions?canceled=true`
    )
  }

  async getSubscriptionSummary(userId: string): Promise<SubscriptionSummary | null> {
    const profile = await this.repository.getUserBillingProfile(userId)
    if (!profile) return null

    const plan = await this.getPlan(profile.planId)
    const quota = profile.quota ?? createQuotaForPlan(plan)

    const subscription: UserSubscription = profile.subscription
      ? { ...profile.subscription }
      : {
          planId: plan.id,
          tier: plan.tier,
          status: 'none',
          stripeCustomerId: profile.stripeCustomerId ?? null,
          stripeSubscriptionId: null,
          currentPeriodEnd: null,
          cancelAt: null,
          renewsAt: null,
          trialEndsAt: null,
          updatedAt: nowISO()
        }

    if (subscription.stripeCustomerId === undefined) {
      subscription.stripeCustomerId = profile.stripeCustomerId ?? null
    }

    const [plansCache, stats, billingContext] = await Promise.all([
      this.loadPlansCache(),
      this.resolveUserStats(userId),
      this.resolveBillingContext(profile, subscription)
    ])

    const { invoices, upcomingInvoice, paymentMethod } = billingContext

    const usageBreakdown = this.buildUsageBreakdown(quota)
    const usageInsights = this.buildUsageInsights(plan, usageBreakdown, profile.insightSnapshot)
    const activity = this.buildActivityEntries(subscription, invoices, quota)

    if (upcomingInvoice) {
      activity.unshift({
        id: `upcoming-${upcomingInvoice.id}`,
        occurredAt: upcomingInvoice.createdAt,
        title: 'Facture à venir',
        description: `Un prélèvement de ${(upcomingInvoice.amountDue / 100).toFixed(2)} ${
          (upcomingInvoice.currency || 'eur').toUpperCase()
        } est programmé.`,
        category: 'billing',
        badge: 'À venir'
      })
    }

    const availablePlans = plansCache.plans.filter((item: any) => item.active)
    const recommendations = this.buildRecommendations(
      plan,
      subscription,
      usageBreakdown,
      availablePlans,
      usageInsights
    )

    const engagement = this.buildEngagement(plan, quota, stats, profile.insightSnapshot)
    const health = this.buildSubscriptionHealth(subscription, upcomingInvoice, invoices, paymentMethod)

    const summary: SubscriptionSummary = {
      subscription,
      plan,
      quota,
      invoices,
      usageBreakdown,
      usageInsights,
      activity,
      recommendations,
      engagement,
      upcomingInvoice,
      paymentMethod,
      health
    }

    const snapshot: BillingInsightSnapshot = {
      capturedAt: nowISO(),
      planId: plan.id,
      status: subscription.status,
      usage: quota.usage,
      engagementScore: engagement?.score,
      completedChecklist: engagement?.checklist.filter(item => item.completed).map(item => item.id)
    }

    try {
      await this.repository.saveInsightSnapshot(userId, snapshot)
    } catch (error) {
      logger.warn('Failed to store billing insight snapshot', { error })
    }

    return summary
  }

  private async resolveBillingContext(
    profile: UserBillingProfile,
    subscription: UserSubscription
  ): Promise<{
    invoices: InvoiceSummary[]
    upcomingInvoice: InvoiceSummary | null
    paymentMethod: SubscriptionPaymentMethod | null
  }> {
    if (!this.isStripeConfigured()) {
      return { invoices: [], upcomingInvoice: null, paymentMethod: null }
    }

    const customerId = profile.stripeCustomerId ?? subscription.stripeCustomerId ?? null
    if (!customerId) {
      return { invoices: [], upcomingInvoice: null, paymentMethod: null }
    }

    const [invoices, upcomingInvoice, paymentMethod] = await Promise.all([
      this.fetchInvoices(customerId),
      this.fetchUpcomingInvoice(customerId, subscription.stripeSubscriptionId ?? null),
      this.fetchDefaultPaymentMethod(customerId)
    ])

    return { invoices, upcomingInvoice, paymentMethod }
  }

  private async resolveUserStats(userId: string): Promise<UserStatsAggregated | null> {
    try {
      const result = await this.usersRepository.getUserStats(userId)
      return result.stats
    } catch (error) {
      logger.warn('Failed to load user stats for engagement scoring', { error })
      return null
    }
  }

  private buildUsageBreakdown(quota: UserQuota): SubscriptionUsageBreakdown[] {
    const items: SubscriptionUsageBreakdown[] = []

    const pushItem = (
      key: SubscriptionUsageBreakdown['key'],
      label: string,
      used: number,
      limit: number,
      unit?: string
    ) => {
      if (!Number.isFinite(limit)) {
        return
      }

      if (limit < 0) {
        items.push({
          key,
          label,
          used: Math.max(used, 0),
          limit: 'unlimited',
          unit,
          percentage: 0,
          remaining: 'unlimited'
        })
        return
      }

      const safeLimit = Math.max(limit, 1)
      const normalizedUsed = Math.max(used, 0)
      const percentage = Math.min(100, Math.round((normalizedUsed / safeLimit) * 100))
      const remaining = Math.max(Number((limit - normalizedUsed).toFixed(unit === 'GB' ? 2 : 0)), 0)

      items.push({
        key,
        label,
        used: normalizedUsed,
        limit,
        unit,
        percentage,
        remaining
      })
    }

    pushItem('plans', 'Plans générés', quota.usage.plansGenerated, quota.limits.plansPerMonth)
    pushItem('ai', 'Appels IA', quota.usage.aiCalls, quota.limits.aiCallsPerMonth)
    pushItem('collaborators', 'Collaborateurs actifs', quota.usage.collaborators, quota.limits.collaborators)

    const storageLimit = quota.limits.storageGB
    if (Number.isFinite(storageLimit)) {
      const usedGB = Number((quota.usage.storageUsedMB / 1024).toFixed(2))
      pushItem('storage', 'Stockage cloud', usedGB, storageLimit, 'GB')
      const lastItem = items[items.length - 1]
      if (lastItem && lastItem.key === 'storage' && lastItem.limit !== 'unlimited') {
        lastItem.limit = Number(storageLimit.toFixed(2))
        lastItem.remaining = Number(Math.max(storageLimit - usedGB, 0).toFixed(2))
      }
    }

    return items
  }

  private buildUsageInsights(
    plan: SubscriptionPlan,
    breakdown: SubscriptionUsageBreakdown[],
    previousSnapshot?: BillingInsightSnapshot | null
  ): SubscriptionInsight[] {
    const insights: SubscriptionInsight[] = []
    const previousUsage = previousSnapshot?.usage

    const previousByKey: Record<string, number | undefined> = {
      plans: previousUsage?.plansGenerated,
      ai: previousUsage?.aiCalls,
      collaborators: previousUsage?.collaborators,
      storage: previousUsage ? Number((previousUsage.storageUsedMB / 1024).toFixed(2)) : undefined
    }

    const buildTrend = (key: keyof typeof previousByKey, current: number, unit?: string) => {
      const previous = previousByKey[key]
      if (previous === undefined) {
        return { trend: 'steady' as const, label: 'Nouvelle mesure' }
      }
      const rawDelta = Number((current - previous).toFixed(unit === 'GB' ? 2 : 0))
      if (rawDelta === 0) {
        return { trend: 'steady' as const, label: 'Stable' }
      }
      const formattedDelta = unit === 'GB'
        ? `${rawDelta > 0 ? '+' : '-'}${Math.abs(rawDelta).toFixed(2)} ${unit}`
        : `${rawDelta > 0 ? '+' : '-'}${Math.abs(rawDelta)}`
      return {
        trend: rawDelta > 0 ? ('up' as const) : ('down' as const),
        label: `${formattedDelta} depuis la dernière visite`
      }
    }

    const formatHighlight = (item: SubscriptionUsageBreakdown) => {
      if (item.limit === 'unlimited') {
        return `${item.used}${item.unit ? ` ${item.unit}` : ''} utilisés`
      }
      return `${item.used}${item.unit ? ` ${item.unit}` : ''} / ${item.limit}${item.unit ? ` ${item.unit}` : ''}`
    }

    const formatRemaining = (item: SubscriptionUsageBreakdown) => {
      if (item.limit === 'unlimited' || item.remaining === 'unlimited') {
        return 'Aucun plafond sur ce quota.'
      }
      if (typeof item.remaining === 'number') {
        const suffix = item.unit ? ` ${item.unit}` : ''
        return `${item.remaining}${suffix} restants sur la période.`
      }
      return ''
    }

    const plansBreakdown = breakdown.find(item => item.key === 'plans')
    if (plansBreakdown) {
      const { trend, label } = buildTrend('plans', plansBreakdown.used)
      insights.push({
        key: 'plans',
        title: `${plansBreakdown.used} plans IA`,
        description: formatRemaining(plansBreakdown),
        highlight: formatHighlight(plansBreakdown),
        icon: 'route',
        progress: plansBreakdown.limit === 'unlimited' ? undefined : plansBreakdown.percentage / 100,
        trend,
        trendLabel: label
      })
    }

    const aiBreakdown = breakdown.find(item => item.key === 'ai')
    if (aiBreakdown) {
      const { trend, label } = buildTrend('ai', aiBreakdown.used)
      insights.push({
        key: 'ai',
        title: `${aiBreakdown.used} appels IA`,
        description: formatRemaining(aiBreakdown),
        highlight: formatHighlight(aiBreakdown),
        icon: 'bolt',
        progress: aiBreakdown.limit === 'unlimited' ? undefined : aiBreakdown.percentage / 100,
        trend,
        trendLabel: label
      })
    }

    const collaboratorsBreakdown = breakdown.find(item => item.key === 'collaborators')
    if (collaboratorsBreakdown) {
      const { trend, label } = buildTrend('collaborators', collaboratorsBreakdown.used)
      insights.push({
        key: 'collaborators',
        title: `${collaboratorsBreakdown.used} collaborateurs`,
        description:
          collaboratorsBreakdown.limit === 'unlimited'
            ? 'Invitez une équipe illimitée sur vos roadbooks.'
            : `${collaboratorsBreakdown.remaining} places disponibles pour votre équipe.`,
        highlight: formatHighlight(collaboratorsBreakdown),
        icon: 'groups',
        progress:
          collaboratorsBreakdown.limit === 'unlimited'
            ? undefined
            : collaboratorsBreakdown.percentage / 100,
        trend,
        trendLabel: label
      })
    }

    const storageBreakdown = breakdown.find(item => item.key === 'storage')
    if (storageBreakdown) {
      const { trend, label } = buildTrend('storage', storageBreakdown.used, 'GB')
      insights.push({
        key: 'storage',
        title: `${storageBreakdown.used} ${storageBreakdown.unit ?? 'GB'} stockés`,
        description: formatRemaining(storageBreakdown),
        highlight: formatHighlight(storageBreakdown),
        icon: 'cloud',
        progress: storageBreakdown.limit === 'unlimited' ? undefined : storageBreakdown.percentage / 100,
        trend,
        trendLabel: label
      })
    }

    if (!insights.length) {
      insights.push({
        key: 'plan',
        title: plan.name,
        description: 'Aucune donnée de consommation disponible pour le moment.',
        highlight: 'Suivi en attente',
        icon: 'insights'
      })
    }

    return insights
  }

  private buildEngagement(
    plan: SubscriptionPlan,
    quota: UserQuota,
    stats: UserStatsAggregated | null,
    previousSnapshot?: BillingInsightSnapshot | null
  ): SubscriptionEngagement {
    const generatedAt = nowISO()
    const plansGenerated = quota.usage.plansGenerated
    const aiCalls = quota.usage.aiCalls
    const collaborators = quota.usage.collaborators
    const tripsCreated = stats?.tripsCreated ?? plansGenerated
    const kmDriven = stats?.kmDriven ?? 0
    const countriesVisited = stats?.countriesVisited ?? 0

    const clampRatio = (value: number, target: number) => {
      if (target <= 0) {
        return value > 0 ? 1 : 0
      }
      return Math.min(1, value / target)
    }

    const planTarget = plan.limits.plansPerMonth < 0
      ? 6
      : Math.max(3, Math.min(plan.limits.plansPerMonth, 10))
    const aiTarget = plan.limits.aiCallsPerMonth < 0
      ? 20
      : Math.max(5, Math.min(plan.limits.aiCallsPerMonth, 50))
    const collaboratorTarget = plan.limits.collaborators < 0
      ? 4
      : Math.max(1, Math.min(plan.limits.collaborators, 5))

    const planRatio = clampRatio(plansGenerated, planTarget)
    const aiRatio = clampRatio(aiCalls, aiTarget)
    const collaboratorRatio = clampRatio(collaborators, collaboratorTarget)
    const tripRatio = clampRatio(tripsCreated, 5)
    const countriesRatio = clampRatio(countriesVisited, 3)

    const weightedScore =
      planRatio * 0.28 +
      aiRatio * 0.22 +
      collaboratorRatio * 0.2 +
      tripRatio * 0.18 +
      countriesRatio * 0.12

    const score = Math.round(Math.min(100, weightedScore * 100))
    const previousScore = previousSnapshot?.engagementScore
    const scoreDelta = typeof previousScore === 'number' ? score - Math.round(previousScore) : null

    let stage: SubscriptionEngagement['stage'] = 'discover'
    if (score >= 70 || (collaborators > 0 && aiCalls >= 8)) {
      stage = 'scale'
    } else if (score >= 40 || plansGenerated >= 2) {
      stage = 'activate'
    }

    const checklist = [
      {
        id: 'first-plan',
        label: 'Créer votre premier plan IA',
        description: 'Lancez un roadbook complet depuis le Planner IA.',
        completed: plansGenerated > 0 || tripsCreated > 0,
        metricKey: 'plans'
      },
      {
        id: 'ai-calls',
        label: 'Explorer les suggestions IA',
        description: 'Débloquez des étapes intelligentes avec au moins 3 appels IA.',
        completed: aiCalls >= 3,
        metricKey: 'ai'
      },
      {
        id: 'invite-collaborator',
        label: 'Inviter un coéquipier',
        description: 'Partagez un plan et collaborez en direct avec votre équipe.',
        completed: collaborators > 0,
        metricKey: 'collaborators'
      },
      {
        id: 'explore-countries',
        label: 'Explorer de nouveaux horizons',
        description: 'Ajoutez des arrêts dans au moins deux pays différents.',
        completed: countriesVisited >= 2,
        metricKey: 'countries'
      }
    ]

    const nextChecklist = checklist.find(item => !item.completed)

    const totalPlans = Math.max(plansGenerated, tripsCreated)
    const milestonePlansProgress = clampRatio(totalPlans, 3)
    const milestoneAiProgress = clampRatio(aiCalls, 10)
    const milestoneTeamProgress = clampRatio(collaborators, Math.max(1, collaboratorTarget))
    const milestoneCountriesProgress = clampRatio(countriesVisited, 3)

    const milestones = [
      {
        id: 'milestone-plans',
        title: '3 plans validés',
        description: 'Stabilisez votre routine de planification avec 3 roadbooks.',
        icon: 'map',
        achieved: milestonePlansProgress >= 1,
        progress: milestonePlansProgress,
        progressLabel: `${totalPlans} / 3 plans`,
        metricKey: 'plans'
      },
      {
        id: 'milestone-ai',
        title: '10 appels IA',
        description: 'Boostez vos itinéraires grâce au copilote intelligent.',
        icon: 'auto_awesome',
        achieved: milestoneAiProgress >= 1,
        progress: milestoneAiProgress,
        progressLabel: `${aiCalls} / 10 appels`,
        metricKey: 'ai'
      },
      {
        id: 'milestone-team',
        title: 'Équipe active',
        description: 'Invitez vos copilotes pour co-construire vos voyages.',
        icon: 'groups',
        achieved: milestoneTeamProgress >= 1,
        progress: milestoneTeamProgress,
        progressLabel: `${collaborators} / ${Math.max(1, collaboratorTarget)} coéquipiers`,
        metricKey: 'collaborators'
      },
      {
        id: 'milestone-explore',
        title: '3 pays explorés',
        description: 'Validez votre statut de globe-trotter avec 3 pays.',
        icon: 'public',
        achieved: milestoneCountriesProgress >= 1,
        progress: milestoneCountriesProgress,
        progressLabel: `${countriesVisited} / 3 pays`,
        metricKey: 'countries'
      }
    ]

    const spotlight = nextChecklist
      ? `Prochaine étape : ${nextChecklist.label}`
      : 'Vous exploitez pleinement votre abonnement. Continuez sur cette lancée !'

    return {
      score,
      scoreDelta,
      stage,
      spotlight,
      stats: {
        plansGenerated,
        aiCalls,
        collaborators,
        tripsCreated,
        kmDriven,
        countriesVisited
      },
      checklist,
      milestones,
      generatedAt
    }
  }

  private buildActivityEntries(
    subscription: UserSubscription,
    invoices: InvoiceSummary[],
    quota: UserQuota
  ): SubscriptionActivityEntry[] {
    const entries: SubscriptionActivityEntry[] = []

    const renewalDate = subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd) : null
    if (renewalDate) {
      entries.push({
        id: 'renewal',
        occurredAt: renewalDate.toISOString(),
        title: 'Renouvellement programmé',
        description: `Renouvellement le ${renewalDate.toLocaleDateString('fr-FR', { dateStyle: 'long' })}`,
        category: 'subscription',
        badge: 'Renouvellement'
      })
    }

    const trialEndsAt = subscription.trialEndsAt ? new Date(subscription.trialEndsAt) : null
    if (subscription.status === 'trialing' && trialEndsAt) {
      entries.push({
        id: 'trial-end',
        occurredAt: trialEndsAt.toISOString(),
        title: 'Fin de période d’essai',
        description: `Votre essai se termine le ${trialEndsAt.toLocaleDateString('fr-FR', { dateStyle: 'long' })}.`,
        category: 'subscription',
        badge: 'Essai'
      })
    }

    const latestInvoice = invoices.length
      ? [...invoices].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
      : null
    if (latestInvoice) {
      entries.push({
        id: `invoice-${latestInvoice.id}`,
        occurredAt: latestInvoice.createdAt,
        title: 'Facture récente',
        description: `Montant ${new Intl.NumberFormat('fr-FR', {
          style: 'currency',
          currency: latestInvoice.currency.toUpperCase()
        }).format(latestInvoice.amountPaid || latestInvoice.amountDue || 0)} (${latestInvoice.status}).`,
        category: 'billing',
        badge: latestInvoice.status === 'paid' ? 'Payée' : undefined
      })
    }

    if (quota.updatedAt) {
      const periodEnd = quota.window?.periodEnd ? new Date(quota.window.periodEnd) : null
      entries.push({
        id: 'quota-update',
        occurredAt: quota.updatedAt,
        title: 'Quotas mis à jour',
        description: periodEnd
          ? `Cycle en cours jusqu’au ${periodEnd.toLocaleDateString('fr-FR', { dateStyle: 'long' })}.`
          : 'Vos quotas ont été recalculés.',
        category: 'usage'
      })
    }

    return entries
      .slice().sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime())
      .slice(0, 6)
  }

  private buildRecommendations(
    plan: SubscriptionPlan,
    subscription: UserSubscription,
    breakdown: SubscriptionUsageBreakdown[],
    availablePlans: SubscriptionPlan[],
    insights: SubscriptionInsight[]
  ): SubscriptionRecommendation[] {
    const recommendations: SubscriptionRecommendation[] = []

    const highUsage = breakdown.filter(
      item => item.limit !== 'unlimited' && typeof item.limit === 'number' && item.percentage >= 80
    )

    if (highUsage.length) {
      const currentPrice = plan.monthlyPrice
      const candidates = availablePlans
        .filter(candidate => candidate.id !== plan.id && candidate.active && candidate.monthlyPrice > currentPrice)
        .slice().sort((a, b) => a.monthlyPrice - b.monthlyPrice)

      const upgradePlan = candidates[0]
      if (upgradePlan) {
        const metric = highUsage[0]
        const featureHighlight = upgradePlan.features[0] ?? 'des capacités avancées'
        recommendations.push({
          id: `upgrade-${upgradePlan.id}`,
          title: `Passez sur ${upgradePlan.name}`,
          description: `Vous avez consommé ${metric.percentage}% de ${metric.label}. ${upgradePlan.name} débloque ${featureHighlight} et augmente vos quotas.`,
          actionLabel: `Choisir ${upgradePlan.name}`,
          actionType: 'upgrade',
          icon: 'trending_up',
          planId: upgradePlan.id,
          metricKey: metric.key
        })
      }
    }

    const now = new Date()
    const trialEndsAt = subscription.trialEndsAt ? new Date(subscription.trialEndsAt) : null
    if (subscription.status === 'trialing' && trialEndsAt) {
      const remainingDays = Math.max(0, Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
      recommendations.push({
        id: 'trial-checklist',
        title: 'Sécurisez votre abonnement',
        description: `Votre essai se termine dans ${remainingDays} jour${remainingDays > 1 ? 's' : ''}. Ajoutez un moyen de paiement ou choisissez un plan définitif pour conserver vos quotas.`,
        actionLabel: 'Ouvrir le portail',
        actionType: 'optimize',
        icon: 'verified_user'
      })
    } else if (subscription.currentPeriodEnd) {
      const renewalDate = new Date(subscription.currentPeriodEnd)
      const daysToRenewal = Math.ceil((renewalDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      if (!Number.isNaN(daysToRenewal) && daysToRenewal <= 7) {
        recommendations.push({
          id: 'renewal-reminder',
          title: 'Renouvellement imminent',
          description: `Votre renouvellement est prévu le ${renewalDate.toLocaleDateString('fr-FR', { dateStyle: 'long' })}. Vérifiez vos informations de paiement dans le portail Stripe.`,
          actionLabel: 'Gérer mon abonnement',
          actionType: 'optimize',
          icon: 'calendar_month'
        })
      }
    }

    if (!recommendations.length && insights.length) {
      const focus = insights[0]
      recommendations.push({
        id: `discover-${focus.key}`,
        title: 'Optimisez votre utilisation',
        description: `Analysez vos métriques (${focus.title}) pour identifier les prochains leviers côté planner et IA.`,
        actionLabel: 'Voir les détails',
        actionType: 'discover',
        icon: 'explore',
        metricKey: focus.key
      })
    }

    return recommendations.slice(0, 3)
  }

  private computeOutstandingBalance(invoice: InvoiceSummary | null): number {
    if (!invoice) {
      return 0
    }
    const amountDue = invoice.amountDue ?? 0
    const amountPaid = invoice.amountPaid ?? 0
    return Math.max(amountDue - amountPaid, 0)
  }

  private buildSubscriptionHealth(
    subscription: UserSubscription,
    upcomingInvoice: InvoiceSummary | null,
    invoices: InvoiceSummary[],
    paymentMethod: SubscriptionPaymentMethod | null
  ): SubscriptionHealth {
    if (!this.isStripeConfigured()) {
      return {
        status: 'ok',
        headline: 'Facturation hors-ligne',
        detail: 'Stripe n’est pas configuré sur cet environnement.',
        outstandingBalance: 0,
        delinquentSince: null,
        hasValidPaymentMethod: false,
        recommendedAction: 'Configurer les clés Stripe',
        updatedAt: nowISO()
      }
    }

    const outstandingBalance = this.computeOutstandingBalance(upcomingInvoice)
    const delinquentInvoice = invoices.find(invoice =>
      ['open', 'past_due', 'uncollectible'].includes(invoice.status)
    )

    let status: SubscriptionHealth['status'] = 'ok'
    let headline = 'Abonnement actif'
    let detail = 'Vos paiements sont à jour.'
    let recommendedAction: string | undefined

    if (['past_due', 'unpaid', 'incomplete', 'incomplete_expired'].includes(subscription.status)) {
      status = 'critical'
      headline = 'Paiement requis'
      detail = 'Le dernier prélèvement a échoué. Mettez à jour votre moyen de paiement.'
      recommendedAction = 'Mettre à jour le moyen de paiement'
    } else if (subscription.cancelAt) {
      status = 'warning'
      headline = 'Résiliation programmée'
      detail = 'Votre abonnement se terminera à la fin de la période en cours.'
      recommendedAction = 'Annuler la résiliation sur le portail Stripe'
    } else if (!paymentMethod && outstandingBalance > 0) {
      status = 'warning'
      headline = 'Aucun moyen de paiement enregistré'
      detail = 'Ajoutez une carte pour garantir le prochain renouvellement.'
      recommendedAction = 'Ajouter un moyen de paiement'
    } else if (delinquentInvoice && delinquentInvoice.status !== 'paid') {
      status = 'warning'
      headline = 'Facture en attente'
      detail = 'Un règlement est nécessaire pour conserver l’accès premium.'
      recommendedAction = 'Régler la facture depuis le portail Stripe'
    } else if (!paymentMethod) {
      status = 'warning'
      headline = 'Ajoutez un moyen de paiement'
      detail = 'Aucune carte par défaut n’est définie pour les futurs prélèvements.'
      recommendedAction = 'Définir une carte par défaut'
    } else if (outstandingBalance > 0) {
      status = 'warning'
      headline = 'Prélèvement à venir'
      detail = 'Un règlement sera bientôt effectué.'
    }

    return {
      status,
      headline,
      detail,
      outstandingBalance,
      delinquentSince: delinquentInvoice?.createdAt ?? null,
      hasValidPaymentMethod: Boolean(paymentMethod),
      recommendedAction,
      updatedAt: nowISO()
    }
  }

  private async fetchInvoices(customerId: string): Promise<InvoiceSummary[]> {
    const stripe = this.requireStripe()
    const invoices = await stripe.invoices.list({
      customer: customerId,
      limit: 12
    })

    return invoices.data.map(invoice => this.mapInvoiceToSummary(invoice))
  }

  private async fetchUpcomingInvoice(
    customerId: string,
    subscriptionId: string | null
  ): Promise<InvoiceSummary | null> {
    const stripe = this.requireStripe()
    try {
      const invoice = await stripe.invoices.retrieveUpcoming({
        customer: customerId,
        subscription: subscriptionId ?? undefined
      })
      return this.mapInvoiceToSummary(invoice)
    } catch (error) {
      if (this.isStripeError(error, 'invoice_upcoming_none') || this.isResourceMissing(error)) {
        return null
      }
      throw error
    }
  }

  private async fetchDefaultPaymentMethod(customerId: string): Promise<SubscriptionPaymentMethod | null> {
    const stripe = this.requireStripe()
    try {
      const customer = await stripe.customers.retrieve(customerId, {
        expand: ['invoice_settings.default_payment_method']
      })

      if (!customer || (customer as Stripe.DeletedCustomer).deleted) {
        return null
      }

      const defaultPayment = (customer as Stripe.Customer).invoice_settings?.default_payment_method
      if (!defaultPayment) {
        return null
      }

      const paymentMethod = typeof defaultPayment === 'string'
        ? await stripe.paymentMethods.retrieve(defaultPayment)
        : defaultPayment

      if (paymentMethod?.card) {
        return {
          brand: paymentMethod.card.brand ?? 'card',
          last4: paymentMethod.card.last4 ?? '0000',
          expMonth: paymentMethod.card.exp_month ?? 0,
          expYear: paymentMethod.card.exp_year ?? 0,
          country: paymentMethod.card.country ?? null
        }
      }

      return null
    } catch (error) {
      if (this.isResourceMissing(error)) {
        return null
      }
      throw error
    }
  }

  private mapInvoiceToSummary(invoice: Stripe.Invoice | Stripe.UpcomingInvoice): InvoiceSummary {
    const createdAt = 'created' in invoice && invoice.created
      ? new Date(invoice.created * 1000).toISOString()
      : nowISO()
    const paidAt = 'status_transitions' in invoice && invoice.status_transitions?.paid_at
      ? new Date(invoice.status_transitions.paid_at * 1000).toISOString()
      : undefined
    const invoiceId = 'id' in invoice && isString(invoice.id)? invoice.id : 'upcoming'
    const status = 'status' in invoice && invoice.status ? invoice.status : 'upcoming'
    const currency = invoice.currency ?? 'eur'

    return {
      id: invoiceId,
      amountDue: invoice.amount_due ?? 0,
      amountPaid: invoice.amount_paid ?? 0,
      currency,
      status,
      hostedInvoiceUrl: 'hosted_invoice_url' in invoice ? invoice.hosted_invoice_url ?? undefined : undefined,
      pdf: 'invoice_pdf' in invoice ? invoice.invoice_pdf ?? undefined : undefined,
      createdAt,
      paidAt,
      lines: invoice.lines?.data?.map((line: Stripe.InvoiceLineItem) => ({
        description: line.description,
        amount: line.amount ?? 0,
        currency: line.currency ?? currency,
        periodStart: line.period?.start ? new Date(line.period.start * 1000).toISOString() : undefined,
        periodEnd: line.period?.end ? new Date(line.period.end * 1000).toISOString() : undefined
      })) ?? []
    }
  }

  async createCheckoutSession(
    userId: string,
    email: string | null,
    planId: string,
    options?: CheckoutOptions
  ): Promise<CheckoutSessionPayload> {
    const plan = await this.getPlan(planId)
    if (!plan.active) {
      throw new Error('Selected plan is inactive')
    }
    if (plan.monthlyPrice <= 0 || !plan.stripePriceId) {
      throw new Error('Selected plan is not billable or missing Stripe price configuration')
    }

    const stripe = this.requireStripe()
    const profile = await this.repository.getUserBillingProfile(userId)

    let customerId: string | undefined = profile?.stripeCustomerId ?? undefined
    if (!customerId) {
      const customer = await stripe.customers.create({
        email: email ?? undefined,
        metadata: { userId }
      })
      const newCustomerId = customer.id
      customerId = newCustomerId
      await this.repository.updateStripeCustomerId(userId, newCustomerId)
    }

    const session = await stripe.checkout.sessions.create({
      customer: customerId,
      mode: 'subscription',
      payment_method_types: ['card'],
      line_items: [
        {
          price: plan.stripePriceId,
          quantity: 1
        }
      ],
      success_url: this.resolveSuccessUrl(options),
      cancel_url: this.resolveCancelUrl(options),
      metadata: {
        userId,
        planId
      },
      subscription_data: {
        metadata: {
          userId,
          planId
        }
      }
    })

    if (!session.url) {
      throw new Error('Stripe did not return a checkout URL')
    }

    return { url: session.url }
  }

  async createBillingPortalSession(userId: string, returnUrl?: string): Promise<BillingPortalPayload> {
    const profile = await this.repository.getUserBillingProfile(userId)
    if (!profile?.stripeCustomerId) {
      throw new Error('User has no Stripe customer configured')
    }

    const stripe = this.requireStripe()
    const session = await stripe.billingPortal.sessions.create({
      customer: profile.stripeCustomerId,
      return_url:
        returnUrl ||
        process.env.BILLING_PORTAL_RETURN_URL ||
        process.env.STRIPE_PORTAL_RETURN_URL ||
        `${getFrontendBaseUrl()}/account`
    })

    return { url: session.url }
  }

  async handleStripeWebhook(signature: string, payload: Buffer): Promise<void> {
    const stripe = this.requireStripe()
    const secret = process.env.STRIPE_WEBHOOK_SECRET
    if (!secret) {
      throw new Error('STRIPE_WEBHOOK_SECRET missing')
    }

    let event: Stripe.Event
    try {
      event = stripe.webhooks.constructEvent(payload, signature, secret)
    } catch (error) {
      logger.error('Invalid Stripe webhook signature', { error })
      throw error
    }

    switch (event.type) {
      case 'customer.subscription.created':
      case 'customer.subscription.updated':
        await this.syncSubscription(event.data.object as Stripe.Subscription)
        break
      case 'customer.subscription.deleted':
        await this.handleSubscriptionCancellation(event.data.object as Stripe.Subscription)
        break
      case 'invoice.payment_succeeded':
        await this.handleInvoicePaid(event.data.object as Stripe.Invoice)
        break
      case 'invoice.payment_failed':
        await this.handleInvoiceFailed(event.data.object as Stripe.Invoice)
        break
      default:
        break
    }
  }

  private async syncSubscription(subscription: Stripe.Subscription): Promise<void> {
    const priceId = subscription.items.data[0]?.price?.id
    const plan = priceId ? await this.getPlanByPriceId(priceId) : null
    if (!plan) {
      logger.warn('Unknown plan for Stripe price', { priceId })
      return
    }

    const userId = subscription.metadata?.userId || null
    let profile: UserBillingProfile | null = null
    if (userId) {
      profile = await this.repository.getUserBillingProfile(userId)
    }
    if (!profile && isString(subscription.customer)) {
      profile = await this.repository.findUserByStripeCustomer(subscription.customer)
    }

    if (!profile) {
      logger.warn('Unable to resolve user for subscription', { subscriptionId: subscription.id })
      return
    }

    // Type-safe access to Stripe.Subscription properties
    const stripeSubscription = subscription as Stripe.Subscription
    const periodEnd = stripeSubscription.current_period_end
      ? new Date(stripeSubscription.current_period_end * 1000).toISOString()
      : null
    const cancelAt = stripeSubscription.cancel_at
      ? new Date(stripeSubscription.cancel_at * 1000).toISOString()
      : null
    const trialEnd = stripeSubscription.trial_end
      ? new Date(stripeSubscription.trial_end * 1000).toISOString()
      : null

    const userSubscription: UserSubscription = {
      planId: plan.id,
      tier: plan.tier,
      status: subscription.status as UserSubscription['status'],
      stripeCustomerId: typeof subscription.customer === 'string' ? subscription.customer : profile.stripeCustomerId,
      stripeSubscriptionId: subscription.id,
      currentPeriodEnd: periodEnd,
      cancelAt,
      renewsAt: periodEnd,
      trialEndsAt: trialEnd,
      updatedAt: nowISO()
    }

    const quota = createQuotaForPlan(plan)
    await this.repository.updateUserSubscription(profile.id, userSubscription, quota)
    await authService.refreshCustomClaims(profile.id)
  }

  private async handleSubscriptionCancellation(subscription: Stripe.Subscription): Promise<void> {
    const customerId = typeof subscription.customer === 'string' ? subscription.customer : null
    const profile = customerId
      ? await this.repository.findUserByStripeCustomer(customerId)
      : null

    const userId = subscription.metadata?.userId || profile?.id
    if (!userId) {
      logger.warn('Subscription cancellation without user', { subscriptionId: subscription.id })
      return
    }

    const freePlan = await this.getPlan('free')
    const userSubscription: UserSubscription = {
      planId: freePlan.id,
      tier: freePlan.tier,
      status: 'canceled',
      stripeCustomerId: customerId ?? profile?.stripeCustomerId ?? null,
      stripeSubscriptionId: null,
      currentPeriodEnd: null,
      cancelAt: null,
      renewsAt: null,
      trialEndsAt: null,
      updatedAt: nowISO()
    }

    const quota = createQuotaForPlan(freePlan)
    await this.repository.updateUserSubscription(userId, userSubscription, quota)
    await authService.refreshCustomClaims(userId)
  }

  private async handleInvoicePaid(invoice: Stripe.Invoice): Promise<void> {
    if (!invoice.customer) return
    if (!this.stripe) return

    const customerId = typeof invoice.customer === 'string' ? invoice.customer : invoice.customer.id
    const profile = await this.repository.findUserByStripeCustomer(customerId)
    if (!profile) return

    // Type-safe access to Stripe.Invoice line items
    const stripeInvoice = invoice as Stripe.Invoice
    const firstLineItem = stripeInvoice.lines.data[0]
    const priceId = firstLineItem?.price?.id
    if (!priceId) return
    const plan = await this.getPlanByPriceId(priceId)
    if (!plan) return

    const quota = createQuotaForPlan(plan)
    const subscription = profile.subscription ?? {
      planId: plan.id,
      tier: plan.tier,
      status: 'active',
      stripeCustomerId: customerId,
      stripeSubscriptionId: typeof stripeInvoice.subscription === 'string' 
        ? stripeInvoice.subscription 
        : stripeInvoice.subscription?.id ?? null,
      currentPeriodEnd: firstLineItem?.period?.end
        ? new Date(firstLineItem.period.end * 1000).toISOString()
        : null,
      cancelAt: null,
      renewsAt: firstLineItem?.period?.end
        ? new Date(invoice.lines.data[0].period!.end * 1000).toISOString()
        : null,
      trialEndsAt: null,
      updatedAt: nowISO()
    }

    await this.repository.updateUserSubscription(profile.id, subscription, quota)
    await authService.refreshCustomClaims(profile.id)
  }

  private async handleInvoiceFailed(invoice: Stripe.Invoice): Promise<void> {
    if (!invoice.customer) return
    const customerId = typeof invoice.customer === 'string' ? invoice.customer : invoice.customer.id
    const profile = await this.repository.findUserByStripeCustomer(customerId)
    if (!profile) return

    logger.warn('Invoice payment failed for user', { userId: profile.id, invoiceId: invoice.id })
  }

  async generateReferralCode(userId: string): Promise<ReferralCode> {
    // Generate cryptographically secure random code
    const { randomBytes } = await import('node:crypto')
    const code = randomBytes(6).toString('base64')
      .toUpperCase()
      .replaceAll(/[^A-Z0-9]/g, '')
      .slice(0, 8) || 'ROADTRIP'
    
    const referral: ReferralCode = {
      code,
      userId,
      createdAt: nowISO(),
      uses: 0,
      rewards: {
        referrer: 'premium_1month',
        referred: 'premium_1month'
      }
    }
    await this.repository.saveReferralCode(referral)
    return referral
  }

  async getReferralLeaderboard(limit = 10): Promise<ReferralLeaderboardEntry[]> {
    return this.repository.listReferralLeaderboard(limit)
  }

  async getBillingMetrics(): Promise<BillingMetrics> {
    const subscriptions = await this.repository.listActiveSubscriptions()
    let mrr = 0
    let totalUsers = 0
    let freeUsers = 0
    let premiumUsers = 0
    let proUsers = 0

    for (const profile of subscriptions) {
      totalUsers += 1
      const plan = await this.getPlan(profile.planId)
      if (plan.tier === 'free') freeUsers += 1
      if (plan.tier === 'premium') premiumUsers += 1
      if (plan.tier === 'pro') proUsers += 1

      if (plan.billingInterval === 'monthly') {
        mrr += plan.monthlyPrice
      } else {
        mrr += plan.monthlyPrice / 12
      }
    }

    const arr = mrr * 12
    const conversionRate = totalUsers === 0 ? 0 : (totalUsers - freeUsers) / totalUsers

    const metrics: BillingMetrics = {
      mrr,
      arr,
      totalUsers,
      freeUsers,
      premiumUsers,
      proUsers,
      conversionRate,
      churnRate: 0,
      newSignups: 0,
      upgrades: 0,
      downgrades: 0,
      cancellations: 0,
      averageLTV: arr / (totalUsers || 1),
      averageSubscriptionLength: 0
    }

    return metrics
  }

  private resolveLimitKey(action: QuotaAction): keyof UserQuota['limits'] {
    switch (action) {
      case 'generate_plan':
        return 'plansPerMonth'
      case 'ai_call':
        return 'aiCallsPerMonth'
      case 'collaborator':
        return 'collaborators'
      case 'storage':
        return 'storageGB'
      default:
        return 'plansPerMonth'
    }
  }

  private resolveUsageKey(action: QuotaAction): keyof UserQuotaUsage {
    switch (action) {
      case 'generate_plan':
        return 'plansGenerated'
      case 'ai_call':
        return 'aiCalls'
      case 'collaborator':
        return 'collaborators'
      case 'storage':
        return 'storageUsedMB'
      default:
        return 'plansGenerated'
    }
  }

  async checkAndConsumeQuota(
    userId: string,
    action: QuotaAction,
    amount = 1,
    consume = true
  ): Promise<QuotaCheckResult> {
    const profile = await this.repository.getUserBillingProfile(userId)
    const plan = profile
      ? await this.resolvePlanForQuota(userId, { planId: profile.planId, tier: profile.subscription?.tier || (profile as any).plan })
      : await this.getPlan('free')
    let quota = profile?.quota

    if (!quota) {
      quota = createQuotaForPlan(plan)
      await this.repository.resetQuota(userId, quota)
    }

    const limitKey = this.resolveLimitKey(action)
    const usageKey = this.resolveUsageKey(action)

    const rawLimit = quota.limits[limitKey]
    if (typeof rawLimit !== 'number') {
      throw new Error(`Missing quota limit for ${limitKey}`)
    }
    const currentUsage = quota.usage[usageKey]

    const isStorage = action === 'storage'
    const normalizedAmount = isStorage ? amount : Math.ceil(amount)
    const effectiveLimit = isStorage && rawLimit >= 0 ? rawLimit * 1024 : rawLimit
    const effectiveUsage = currentUsage

    const remaining = effectiveLimit < 0
      ? null
      : effectiveLimit - effectiveUsage

    if (effectiveLimit >= 0 && effectiveUsage + normalizedAmount > effectiveLimit) {
      return {
        allowed: false,
        quota,
        limits: quota.limits,
        usage: quota.usage,
        remaining: remaining !== null ? Math.max(0, remaining) : null,
        reason: 'quota_exceeded'
      }
    }

    if (consume) {
      const increments: Record<keyof UserQuotaUsage, number | undefined> = {
        plansGenerated: undefined,
        aiCalls: undefined,
        collaborators: undefined,
        storageUsedMB: undefined
      }
      increments[usageKey] = normalizedAmount
      quota = await this.repository.incrementQuotaUsage(userId, increments)
    }

    return {
      allowed: true,
      quota,
      limits: quota.limits,
      usage: quota.usage,
      remaining: remaining !== null ? Math.max(0, remaining - normalizedAmount) : null
    }
  }
}
